import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class LightningDashboardService {

  constructor(private http: HttpClient) { }

  getHeirarchy(postData: any) {
    return this.http.post<any>(environment.url.HEATWAVE_STAGING_API_URL + 'locations/heirarchy', postData);
  }

  getLightningTableData(payload: any) {
    return this.http.post(
      environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getLightningTableData',
      payload
    );
  }

  getLightningHeatMapData(refTs: number, forecastLength: string) {
    return this.http.get(
      environment.url.HEATWAVE_STAGING_API_URL + `weatherForecast/getLightningHeatMapData/${refTs}/${forecastLength}`
    );
  }

  getHeatMapImageBounds(heatmapimageBounds: any) {
    let latlongs: any = [];
    let imageBounds: any = []

    latlongs.push(heatmapimageBounds['southWest']['latitude']);
    latlongs.push(heatmapimageBounds['southWest']['longitude']);
    imageBounds.push(latlongs);
    latlongs = [];
    latlongs.push(heatmapimageBounds['northEast']['latitude']);
    latlongs.push(heatmapimageBounds['northEast']['longitude']);
    imageBounds.push(latlongs);
    return imageBounds;
  }

  // Returns 4-corner anchors for L.imageTransform: [topLeft, topRight, bottomRight, bottomLeft]
  getImageBounds(imageBounds: any[]): any[] {
    return [
      [imageBounds[1][0], imageBounds[0][1]],
      [imageBounds[1][0], imageBounds[1][1]],
      [imageBounds[0][0], imageBounds[1][1]],
      [imageBounds[0][0], imageBounds[0][1]]
    ];
  }

  getRefTimestamp(): number {
    const today = new Date();
    today.setHours(8, 30, 0, 0);
    return today.getTime();
  }

  getCurrentRefTs(): number {
    const now = new Date();
    const result = new Date(now);
    if (now.getMinutes() >= 30) {
      result.setMinutes(30, 0, 0);
    } else {
      result.setHours(now.getHours() - 1, 30, 0, 0);
    }
    return result.getTime();
  }

  get24HrsTimeStamps(): any {
    const now = new Date();
    const start = new Date(now);
    if (now.getMinutes() >= 30) {
      start.setMinutes(30, 0, 0);
    } else {
      start.setHours(now.getHours() - 1, 30, 0, 0);
    }
    const dayStartTs = start.getTime();
    const blocks: any = {};
    for (let i = 0; i < 24; i++) {
      blocks[i] = {};
      blocks[i]['timeStamp'] = dayStartTs + (i * 3600000);
      blocks[i]['timeLineName'] = {};
      blocks[i]['timeLineName']['toolTip'] = this.convertTimestampToFullBlockValue(blocks[i]['timeStamp']);
      blocks[i]['timeLineName']['label'] = this.convertTimestampToBlockValue(blocks[i]['timeStamp']);
      blocks[i]['leftSideName'] = this.convertTimestampToFullBlockValue(blocks[i]['timeStamp']);
      blocks[i]['timeLinePos'] = i;
    }
    return blocks;
  }

  get7DaysTimeStamps(): any {
    const today = new Date();
    today.setHours(8, 30, 0, 0);
    const dayStartTs = today.getTime();
    const blocks: any = {};
    for (let i = 0; i < 7; i++) {
      blocks[i] = {};
      blocks[i]['timeStamp'] = dayStartTs + (i * 86400000);
      const d = new Date(blocks[i]['timeStamp']);
      blocks[i]['yyyymmdd'] = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
      blocks[i]['timeLineName'] = {};
      blocks[i]['timeLineName']['label'] = this.convetTimeStampToDay(blocks[i]['timeStamp']);
      blocks[i]['timeLineName']['toolTip'] = '8:30 AM - 8:30 AM';
      blocks[i]['leftSideName'] = this.convetTimeStampToDay(blocks[i]['timeStamp']);
      blocks[i]['timeLinePos'] = i;
    }
    return blocks;
  }

  getCurrentBlockTs(dayStartTs: number): number {
    const currentTs = new Date().getTime();
    let blockTs = dayStartTs;
    let timeStamp = dayStartTs;
    for (let i = 0; i < 8; i++) {
      if (blockTs <= currentTs) {
        timeStamp = blockTs;
      } else {
        break;
      }
      blockTs += 10800000;
    }
    return timeStamp;
  }

  getCurrentBlockIndex(blocks: any): number {
    const currentTime = new Date().getTime();
    let currentBlockIndex = 0;
    for (const index in blocks) {
      const blockStartTime = blocks[index].timeStamp;
      const blockEndTime = blockStartTime + 3 * 60 * 60 * 1000;
      if (currentTime >= blockStartTime && currentTime < blockEndTime) {
        currentBlockIndex = parseInt(index);
        break;
      }
    }
    return currentBlockIndex;
  }

  convertTimestampToBlockValue(ts: number): string {
    ts = Number(ts);
    let hours = new Date(ts).getHours();
    const minutes = new Date(ts).getMinutes();
    const amPm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${String(minutes).padStart(2, '0')} ${amPm}`;
  }

  convertTimestampToFullBlockValue(ts: number): string {
    const start = this.convertTimestampToBlockValue(ts);
    const end = this.convertTimestampToBlockValue(ts + 10800000);
    return `${start} - ${end}`;
  }

  convetTimeStampToDay(ts: number): string {
    return new Date(ts).toLocaleDateString();
  }

  getObservedLightningHeatMapData(date: string) {
    return this.http.get<any>(
      environment.url.HEATWAVE_STAGING_API_URL + `lightning/getLightningHeatMapData/IITM/${date}`
    );
  }

  // ── New time-block based observed APIs ────────────────────────────────────
  getTimeBlocks() {
    return this.http.get<number[]>(environment.url.HEATWAVE_STAGING_API_URL + 'lightning/timeBlocks');
  }

  getObservedLightningHeatMapDataWithRange(date: string, startTs: number, endTs: number) {
    return this.http.get<any>(
      environment.url.HEATWAVE_STAGING_API_URL + `lightning/getLightningHeatMapData/IITM/${date}/${startTs}/${endTs}`
    );
  }

  convertTimestampToHourlyLabel(ts: number): { range: string; period: string } {
    const d = new Date(ts);
    const startH = d.getHours();
    const endH = (startH + 1) % 24;
    const period = startH >= 12 ? 'PM' : 'AM';
    const startHr = startH % 12 || 12;
    const endHr = endH % 12 || 12;
    return {
      range: `${String(startHr).padStart(2, '0')}-${String(endHr).padStart(2, '0')}`,
      period
    };
  }

  getLightningAggregatedData(payload: any) {
    return this.http.post<any>(
      environment.url.HEATWAVE_STAGING_API_URL + 'lightning/getLightningAggregatedData',
      payload
    );
  }

  // getLightningObservedChartData(payload: any) {
  //   return this.http.post<any>(
  //     environment.url.HEATWAVE_STAGING_API_URL + 'lightning/getLightningChartData',
  //     payload
  //   );
  // }

  transformAggregatedDataForMiniChart(data: any, dataField: 'categoryAreaPercentage' | 'categoryCount' = 'categoryCount'): any {
    const catMap: any = { No: 'No Lightning', Light: 'Light', Moderate: 'Moderate', Severe: 'Severe' };
    const slotKey = 'aggregated';
    const result: any = {};
    const tsCountMap: any = {};
    Object.values(catMap).forEach((display: any) => { result[display] = { [slotKey]: {} }; });

    Object.values(data).forEach((entry: any) => {
      if (!entry.dataMap) { return; }
      Object.keys(entry.dataMap).forEach(ts => {
        const slot = entry.dataMap[ts];
        if (slot && slot[dataField]) {
          tsCountMap[ts] = (tsCountMap[ts] || 0) + 1;
          Object.keys(catMap).forEach(apiCat => {
            result[catMap[apiCat]][slotKey][ts] =
              (result[catMap[apiCat]][slotKey][ts] || 0) + (slot[dataField][apiCat] || 0);
          });
        }
      });
    });

    if (dataField === 'categoryAreaPercentage') {
      Object.keys(catMap).forEach(apiCat => {
        const display = catMap[apiCat];
        Object.keys(result[display][slotKey]).forEach(ts => {
          result[display][slotKey][ts] /= (tsCountMap[ts] || 1);
        });
      });
    }

    return result;
  }

  transformObservedDataForMiniChart(data: any): any {
    const catMap: any = { No: 'No Lightning', Light: 'Light', Moderate: 'Moderate', Severe: 'Severe' };
    const result: any = {};
    Object.values(catMap).forEach((display: any) => { result[display] = { observed: {} }; });
    Object.keys(data).forEach(ts => {
      Object.keys(catMap).forEach(apiCat => {
        result[catMap[apiCat]]['observed'][ts] = data[ts][apiCat] || 0;
      });
    });
    return result;
  }

  getCurrentDateYyyymmdd(): string {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  }

  getAlertCategory(value: number): string {
    if (value < 0.001) return 'No Lightning';
    if (value < 20) return 'Light';
    if (value < 60) return 'Moderate';
    if (value > 60) return 'Severe';
    return 'Severe';
  }

  getLightningAlertCounts(payload: any) {
    return this.http.post<any>(
      environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getLightningAlertCounts',
      payload
    );
  }

  // getLightningChartData(payload: any) {
  //   return this.http.post<any>(
  //     environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getLightningChartData',
  //     payload
  //   );
  // }

  getLatestLightningForecastFile() {
    return this.http.get<any>(
      environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getLatestLightningForecastFile'
    );
  }

  getForecastLightningAggregatedData(payload: any) {
    return this.http.post<any>(
      environment.url.HEATWAVE_STAGING_API_URL + 'weatherForecast/getForecastLightningAggregatedData',
      payload
    );
  }

  buildLightningChartOptions(data: any, forecastLength: string, currentRefTs?: number, showDataLabels = true): any {
    const categoryOrder = ['No Lightning', 'Light', 'Moderate', 'Severe'];
    const colorMap: any = {
      'No Lightning': '#FFFFFF',
      'Light': '#f6f94b',
      'Moderate': '#fdba12',
      'Severe': '#f92509',
    };

    const firstCat = Object.keys(data)[0];
    const slotKey = firstCat ? Object.keys(data[firstCat])[0] : null;
    if (!slotKey) return null;

    const tsSet = new Set<string>();
    categoryOrder.forEach(cat => {
      if (data[cat] && data[cat][slotKey]) {
        Object.keys(data[cat][slotKey]).forEach(ts => tsSet.add(ts));
      }
    });
    const isYyyymmdd = (ts: string) => /^\d{8}$/.test(ts);

    const parseDate = (ts: string): Date => {
      if (isYyyymmdd(ts)) {
        return new Date(`${ts.substr(0, 4)}-${ts.substr(4, 2)}-${ts.substr(6, 2)}`);
      }
      return new Date(+ts);
    };

    const is24h = forecastLength === '24';
    let timestamps = Array.from(tsSet).sort((a, b) => parseDate(a).getTime() - parseDate(b).getTime());
    if (is24h && currentRefTs > 0) {
      timestamps = timestamps.filter(ts => parseDate(ts).getTime() >= currentRefTs);
    }

    const categories = timestamps.map(ts => {
      const d = parseDate(ts);
      if (is24h) {
        let h = d.getHours();
        const amPm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${amPm}`;
      }
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    });

    const series = categoryOrder.map(cat => ({
      name: cat,
      data: timestamps.map(ts => (data[cat] && data[cat][slotKey] && data[cat][slotKey][ts] != null) ? +data[cat][slotKey][ts] : 0),
      color: colorMap[cat],
      showInLegend: cat !== 'No Lightning'
    }));

    return {
      chart: { type: 'column', backgroundColor: '#fff', height: 350 },
      title: { text: 'Category Wise Lightning Distribution', style: { fontSize: '13px', fontWeight: 'bold', color: '#1a1a1a' }, align: 'left' },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        categories,
        labels: { rotation: -45, style: { fontSize: '10px' } }
      },
      yAxis: {
        min: 0,
        title: { text: 'No. of Locations', style: { fontSize: '11px' } },
        labels: { style: { fontSize: '10px' } },
        gridLineDashStyle: 'Dash',
        gridLineColor: '#dddddd'
      },
      legend: {
        enabled: true,
        align: 'left',
        x: 40,
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: { fontSize: '10px', fontWeight: 'normal' }
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0.08,
          borderColor: '#000',
          dataLabels: {
            enabled: showDataLabels,
            style: { fontSize: '9px', fontWeight: 'bold', textOutline: 'none' }
          }
        }
      },
      tooltip: {
        shared: true,
        backgroundColor: '#1a1a1a',
        borderColor: '#333',
        borderRadius: 6,
        style: { color: '#fff', fontSize: '11px' },
        headerFormat: '<b style="color:#fff">{point.key}</b><br/>',
        pointFormat: '<span style="color:{series.color}">●</span> <span style="color:#ccc">{series.name}:</span> <b style="color:#fff">{point.y}</b><br/>'
      },
      series
    };
  }

  buildLightningTrendChartOptions(entry: any, formatFn: (ts: any) => string, currentRefTs?: number): any {
    let slots = (entry.table_data || []).slice().sort((a: any, b: any) =>
      String(a.timeStamp).localeCompare(String(b.timeStamp), undefined, { numeric: true })
    );
    if (currentRefTs > 0) {
      slots = slots.filter((s: any) => +s.timeStamp >= currentRefTs);
    }
    if (!slots.length) return null;

    const categories = slots.map((s: any) => formatFn(s.timeStamp));
    const values = slots.map((s: any) =>
      s.data && s.data.lightningValue != null ? +s.data.lightningValue : null
    );

    const zoneColor = (v: number | null) => {
      if (v == null) return '#aaa';
      if (v < 20) return '#f6f94b';
      if (v < 60) return '#fdba12';
      return '#f92509';
    };

    const pointData = values.map((v: number | null) => ({
      y: v,
      marker: { fillColor: zoneColor(v), lineColor: '#555', lineWidth: 1, radius: 5, symbol: 'circle' }
    }));

    const maxVal = Math.max(60, ...values.filter((v: number | null): v is number => v != null));

    return {
      chart: { type: 'line', backgroundColor: '#fff', height: 260 },
      title: {
        text: `${entry.name} — Lightning Risk Trend`,
        style: { fontSize: '12px', fontWeight: 'bold', color: '#1a1a1a' },
        align: 'left'
      },
      credits: { enabled: false },
      exporting: { enabled: false },   
      xAxis: {
        categories,
        labels: { rotation: -45, style: { fontSize: '9px' } }
      },
      yAxis: {
        min: 0,
        max: maxVal * 1.1,
        title: { text: 'Lightning Index', style: { fontSize: '10px' } },
        tickPositions: [0, 20, 60],
        labels: {
          formatter: function () {
            const v = (this as any).value;
            if (v === 0) return '0';
            if (v === 20) return '20';
            if (v === 60) return '60';
            return String(v);
          },
          style: { fontSize: '9px' }
        },
        plotLines: [
          {
            value: 20, color: '#fdba12', dashStyle: 'Dash', width: 1.5, zIndex: 3,
            label: { text: '20', style: { fontSize: '9px', color: '#888' }, align: 'right', x: -4 }
          },
          {
            value: 60, color: '#f92509', dashStyle: 'Dash', width: 1.5, zIndex: 3,
            label: { text: '60', style: { fontSize: '9px', color: '#888' }, align: 'right', x: -4 }
          }
        ],
        plotBands: [
          { from: 0, to: 20, color: 'rgba(246,249,75,0.18)' },
          { from: 20, to: 60, color: 'rgba(253,186,18,0.15)' },
          { from: 60, to: 100000, color: 'rgba(249,37,9,0.12)' }
        ]
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: { fontSize: '10px', fontWeight: 'normal', color: '#444' }
      },
      tooltip: {
        backgroundColor: '#1a1a1a',
        borderColor: '#333',
        borderRadius: 6,
        style: { color: '#fff', fontSize: '11px' },
        formatter: function () {
          const pt = (this as any);
          const val = pt.y != null ? (+pt.y).toFixed(2) : '-';
          let zone = 'Light';
          if (pt.y >= 60) zone = 'Severe';
          else if (pt.y >= 20) zone = 'Moderate';
          return `<b style="color:#fff">${pt.key}</b><br/><span style="color:#ccc">Index:</span> <b style="color:#fff">${val}</b><br/><span style="color:#ccc">Category:</span> <b style="color:#fff">${zone}</b>`;
        }
      },
      plotOptions: {
        line: { lineWidth: 2, color: '#555', connectNulls: false, marker: { enabled: true } },
        scatter: { marker: { symbol: 'square', radius: 6, enabled: true } }
      },
      series: [
        { type: 'line', name: 'Lightning Index', data: pointData, color: '#555', showInLegend: false },
        { type: 'scatter', name: 'Light (0-20)', data: [], color: '#f6f94b', showInLegend: true },
        { type: 'scatter', name: 'Moderate (20-60)', data: [], color: '#fdba12', showInLegend: true },
        { type: 'scatter', name: 'Severe (>60)', data: [], color: '#f92509', showInLegend: true }
      ]
    };
  }

  buildAggregatedChartOptions(data: any, dataField: 'categoryAreaPercentage' | 'categoryCount', filterByCurrentTime = false): any {
    const categoryKeys = ['No', 'Light', 'Moderate', 'Severe'];
    const displayNames: any = { No: 'No Lightning', Light: 'Light', Moderate: 'Moderate', Severe: 'Severe' };
    const colorMap: any = { No: '#FFFFFF', Light: '#f6f94b', Moderate: '#fdba12', Severe: '#f92509' };

    const tsSet = new Set<string>();
    Object.values(data).forEach((entry: any) => {
      if (entry.dataMap) { Object.keys(entry.dataMap).forEach(ts => tsSet.add(ts)); }
    });
    const timestamps = Array.from(tsSet).sort((a, b) => +a - +b);
    if (!timestamps.length) { return null; }

    const agg: any = {};
    const uuidCountPerTs: any = {};
    categoryKeys.forEach(cat => {
      agg[cat] = {};
      timestamps.forEach(ts => { agg[cat][ts] = 0; });
    });
    timestamps.forEach(ts => { uuidCountPerTs[ts] = 0; });

    Object.values(data).forEach((entry: any) => {
      if (!entry.dataMap) { return; }
      timestamps.forEach(ts => {
        const slot = entry.dataMap[ts];
        if (slot && slot[dataField]) {
          uuidCountPerTs[ts]++;
          categoryKeys.forEach(cat => { agg[cat][ts] += (slot[dataField][cat] || 0); });
        }
      });
    });

    const isPercentage = dataField === 'categoryAreaPercentage';
    if (isPercentage) {
      timestamps.forEach(ts => {
        const n = uuidCountPerTs[ts] || 1;
        categoryKeys.forEach(cat => { agg[cat][ts] = agg[cat][ts] / n; });
      });
    }
    const isYyyymmdd = (ts: string) => /^\d{8}$/.test(ts);
    const parseDate = (ts: string): Date => isYyyymmdd(ts)
      ? new Date(`${ts.substr(0, 4)}-${ts.substr(4, 2)}-${ts.substr(6, 2)}`)
      : new Date(+ts);
    const categories = timestamps.map(ts => {
      const d = parseDate(ts);
      if (isYyyymmdd(ts)) {
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
      }
      let h = d.getHours();
      const amPm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${amPm}`;
    });

    // Forecast tab only: filter timestamps to what should be visible.
    // Observed tab always receives the full unmodified arrays (filterByCurrentTime = false).
    let visibleTimestamps = timestamps;
    let visibleCategories = categories;
    if (filterByCurrentTime && !isYyyymmdd(timestamps[0])) {
      // +24H: epoch ms timestamps — show from the last past slot onwards
      const currentTime = Date.now();
      let startIndex = 0;
      for (let i = 0; i < timestamps.length; i++) {
        if (+timestamps[i] <= currentTime) { startIndex = i; } else { break; }
      }
      visibleTimestamps = timestamps.slice(startIndex);
      visibleCategories = categories.slice(startIndex);
    } else if (filterByCurrentTime && isYyyymmdd(timestamps[0])) {
      // +7D: yyyymmdd — show today through today+6 days only
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const todayStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
      const cutoffDate = new Date(now);
      cutoffDate.setDate(cutoffDate.getDate() + 6);
      const cutoffStr = `${cutoffDate.getFullYear()}${pad(cutoffDate.getMonth() + 1)}${pad(cutoffDate.getDate())}`;
      const keepIdx = timestamps.reduce((acc: number[], ts, i) => {
        if (ts >= todayStr && ts <= cutoffStr) { acc.push(i); }
        return acc;
      }, []);
      visibleTimestamps = keepIdx.map(i => timestamps[i]);
      visibleCategories = keepIdx.map(i => categories[i]);
    }

    const series = categoryKeys.map(cat => ({
      name: displayNames[cat],
      data: visibleTimestamps.map(ts => parseFloat((agg[cat][ts] || 0).toFixed(2))),
      color: colorMap[cat],
      showInLegend: cat !== 'No'
    }));

    const yAxisConfig: any = {
      min: 0,
      title: { text: isPercentage ? '% of Area' : 'No. of Locations', style: { fontSize: '11px' } },
      labels: { style: { fontSize: '10px' } },
      gridLineDashStyle: 'Dash',
      gridLineColor: '#dddddd'
    };
    if (isPercentage) {
      yAxisConfig.max = 100;
      yAxisConfig.labels.format = '{value}%';
    }

    return {
      chart: { type: 'column', backgroundColor: '#fff', height: 320 },
      title: { text: null },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: { categories: visibleCategories, labels: { rotation: -45, style: { fontSize: '10px' } } },
      yAxis: yAxisConfig,
      legend: {
        enabled: true, align: 'left', x: 30, verticalAlign: 'bottom', layout: 'horizontal',
        itemStyle: { fontSize: '10px', fontWeight: 'normal' }
      },
      plotOptions: {
        column: {
          stacking: 'normal', borderWidth: 0.08, borderColor: '#000',
          dataLabels: { enabled: false }
        }
      },
      tooltip: {
        shared: true,
        backgroundColor: '#1a1a1a', borderColor: '#333', borderRadius: 6,
        style: { color: '#fff', fontSize: '11px' },
        headerFormat: '<b style="color:#fff">{point.key}</b><br/>',
        pointFormat: isPercentage
          ? '<span style="color:{series.color}">●</span> <span style="color:#ccc">{series.name}:</span> <b style="color:#fff">{point.y:.2f}%</b><br/>'
          : '<span style="color:{series.color}">●</span> <span style="color:#ccc">{series.name}:</span> <b style="color:#fff">{point.y}</b><br/>'
      },
      series
    };
  }

  getAlertBgColor(alert: string): string {
    const colorMap: any = {
      'No Lightning': '#FFFFFF',
      'Light': '#f6f94b',
      'Moderate': '#fdba12',
      'Severe': '#f92509',
    };
    return colorMap[alert] || 'transparent';
  }

  getHeatMap(url: any) {
    let dataPromise = new Promise((resolve, reject) => {
      this.http.get(url).subscribe(
        data => {
          let obj = this.setMapData(data);
          resolve(obj);
        });
    });
    return dataPromise;
  }

  setMapData(data: any) {
    let obj: any = {};
    let imageUrl = {};
    let imageBounds = [];
    for (let block of Object.keys(data)) {
      for (let property of Object.keys(data[block])) {
        imageUrl = data[block]['heatMapImage'];
        let latlongs = [];
        latlongs.push(data[block]['heatMapImageBound']['southWest']['latitude']);
        latlongs.push(data[block]['heatMapImageBound']['southWest']['longitude']);
        imageBounds.push(latlongs);
        latlongs = [];
        latlongs.push(data[block]['heatMapImageBound']['northEast']['latitude']);
        latlongs.push(data[block]['heatMapImageBound']['northEast']['longitude']);
        imageBounds.push(latlongs);
      }
      obj['url'] = imageUrl;
      obj['bounds'] = imageBounds;
    }
    return obj;
  }

  formatDateToMonthYear(dateStr: any) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const label = monthNames[Number(month) - 1] + ' ' + year;
    return label
  }
  setMetaInfo() {
    let metaInfo = JSON.stringify({
      "userId": "689f8ae3-bd85-4b5f-975f-2901fd5f5f7f",
      "username": "vassar",
      "firstName": "Aswin",
      "lastName": "Kumar",
      "email": "aswin@vassarlabs.com",
      "mobileNo": "+918897246760",
      "userDetailsJson": {
        "data": {
          "locType": "Country",
          "location": {
            "country": [
              {
                "countryName": "INDIA",
                "countryUUID": "00cb31c7-2928-4e6d-adcc-443d478c90d6"
              }
            ]
          }
        },
        "scope": null
      },
      "lastLoginTs": "2025-04-17T07:22:26.214+00:00",
      "status": true,
      "customerId": "f7d86063-2db1-4dd7-bbdf-0e4dc6031744",
      "customerName": "heatwavecustomer"
    })
    localStorage.setItem('metaInfo', metaInfo);
  }

}
